import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FiPlus, FiTrash2, FiRefreshCw, FiZap } from "react-icons/fi";
import { getDownloadURL, listAll, ref as storageRef } from "firebase/storage";
import { storage } from "../../services/firebase";
import { useAuth } from "../../context/AuthContext";
import { theme } from "../../styles/theme";
import { Button } from "../ui/Button";
import {
  addActorImages,
  deleteActorByPath,
  generateActorsFromImages,
} from "../../services/api";
import GenerateActorsDialog from "./GenerateActorsDialog";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: ${theme.spacing.sm};
`;

const Card = styled.div`
  border: ${theme.borders.base};
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xs};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const Preview = styled.img`
  width: 100%;
  aspect-ratio: 3 / 4;
  object-fit: cover;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.tertiary};
`;

function ActorsPanel({ onBanner }) {
  const { currentUser } = useAuth();
  const [actors, setActors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [isGenerateOpen, setGenerateOpen] = useState(false);
  const fileInputRef = useRef(null);

  const loadActors = async () => {
    if (!currentUser) {
      setActors([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const baseRef = storageRef(storage, `user_data/${currentUser.uid}/actors`);
      const result = await listAll(baseRef);
      const rows = await Promise.all(
        result.items.map(async (itemRef) => ({
          fullPath: itemRef.fullPath,
          url: await getDownloadURL(itemRef),
        }))
      );
      const ordered = [...rows].sort((a, b) => b.fullPath.localeCompare(a.fullPath));
      setActors(ordered);
    } catch (error) {
      onBanner?.(error.message || "Failed to load actors");
      setActors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadActors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const handleAddActors = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      await addActorImages(files);
      await loadActors();
    } catch (error) {
      onBanner?.(error.message || "Failed to add actors");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteActor = async (actor) => {
    if (!window.confirm("Delete this actor?")) {
      return;
    }
    try {
      await deleteActorByPath(actor.fullPath);
      setActors((prev) => prev.filter((entry) => entry.fullPath !== actor.fullPath));
    } catch (error) {
      onBanner?.(error.message || "Failed to delete actor");
    }
  };

  const handleGenerateActors = async ({ files, count }) => {
    setGenerating(true);
    try {
      const result = await generateActorsFromImages(files, count);
      await loadActors();
      const generatedCount = result?.generated_count || 0;
      const failedCount = result?.failed_count || 0;
      if (failedCount) {
        onBanner?.(`Generated ${generatedCount} actors. ${failedCount} failed.`);
      }
    } catch (error) {
      onBanner?.(error.message || "Failed to generate actors");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Wrapper>
      <Toolbar>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={(event) => {
            const files = Array.from(event.target.files || []);
            handleAddActors(files);
            event.target.value = "";
          }}
          disabled={uploading}
        />
        <Button
          size="small"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          <FiPlus /> {uploading ? "Uploading..." : "Add actor"}
        </Button>
        <Button
          size="small"
          variant="secondary"
          onClick={() => setGenerateOpen(true)}
          disabled={generating}
        >
          <FiZap /> Generate actors
        </Button>
        <Button size="small" variant="ghost" onClick={loadActors} disabled={loading}>
          <FiRefreshCw /> Refresh
        </Button>
      </Toolbar>

      {loading ? (
        <span style={{ color: theme.colors.text.muted }}>Loading actors...</span>
      ) : actors.length ? (
        <Grid>
          {actors.map((actor) => (
            <Card key={actor.fullPath}>
              <Preview src={actor.url} alt="Actor" />
              <Button
                size="small"
                variant="ghost"
                onClick={() => handleDeleteActor(actor)}
              >
                <FiTrash2 /> Remove
              </Button>
            </Card>
          ))}
        </Grid>
      ) : (
        <span style={{ color: theme.colors.text.muted }}>
          No actors yet. Add one or generate a new set.
        </span>
      )}

      <GenerateActorsDialog
        isOpen={isGenerateOpen}
        loading={generating}
        onClose={() => setGenerateOpen(false)}
        onSubmit={handleGenerateActors}
      />
    </Wrapper>
  );
}

export default ActorsPanel;
